/**
 * Team Admin → Hudl.
 *
 * A plain web page (not the touch entry surface): a header, an Export card and
 * an Import card side by side on wide screens (stacked on tablet portrait and
 * phone), and a short list of recent imports. Reviewing an import against an
 * existing game takes over the full page width until it is applied or
 * cancelled.
 *
 * Below the Varsity plan the Hudl service answers 402; the page then shows the
 * upgrade panel instead of controls that cannot work.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useSession } from '../../app/session-context';
import { DataTable, type DataTableColumn, type DataTableSort } from '../../components/data-table';
import { DeskSection } from '../../components/desk-section';
import { PageHeader } from '../../components/page-header';
import { TierLimitCta } from '../../components/tier-limit-cta';
import type { HudlImportRecord } from '../../lib/api-types';

import { ExportCard } from './export-card';
import { formatWhen } from './hudl-format';
import { gamePath, ImportCard, type ReconcileHandoff } from './import-card';
import { ReconcileView } from './reconcile-view';
import { useHudlPage } from './use-hudl-page';

export function IntegrationsPage(): JSX.Element {
  const page = useHudlPage();
  const { tenantSlug } = useSession();
  const [reconcile, setReconcile] = useState<ReconcileHandoff | null>(null);
  const [recentSort, setRecentSort] = useState<DataTableSort>({ by: 'when', order: 'desc' });
  const sortedImports = useMemo(
    () => sortImports(page.imports, recentSort),
    [page.imports, recentSort]
  );

  const recentColumns: DataTableColumn<HudlImportRecord>[] = [
    {
      id: 'game',
      header: 'Game',
      isSortable: true,
      isPinned: true,
      minWidth: 180,
      maxWidth: 360,
      grow: 3,
      render: (record) => (
        <Link to={gamePath(tenantSlug, record.event_id)} className="hudl-recent__game">
          {record.event_name}
        </Link>
      ),
    },
    {
      id: 'file',
      header: 'File',
      isSortable: true,
      priority: 3,
      minWidth: 200,
      grow: 4,
      render: (record) => (
        <span className="hudl-recent__file" title={record.file_name}>
          {record.file_name}
        </span>
      ),
      renderDetail: (record) => record.file_name,
    },
    {
      id: 'added',
      header: 'Plays added',
      isSortable: true,
      align: 'end',
      priority: 1,
      minWidth: 140,
      maxWidth: 150,
      grow: 0,
      render: (record) => <span className="hudl-recent__num">{addedCount(record)}</span>,
    },
    {
      id: 'changes',
      header: 'Other changes',
      priority: 4,
      minWidth: 170,
      grow: 1,
      render: (record) => <span className="muted">{changesLabel(record)}</span>,
    },
    {
      id: 'by',
      header: 'Imported by',
      isSortable: true,
      priority: 5,
      minWidth: 130,
      grow: 1,
      render: (record) => record.created_by_name ?? 'A coach',
    },
    {
      id: 'when',
      header: 'When',
      isSortable: true,
      priority: 2,
      minWidth: 150,
      grow: 1,
      render: (record) => formatWhen(record.created_at),
    },
  ];

  if (page.tierLimitError !== null) {
    const details = page.tierLimitError.details ?? {};
    return (
      <div className="hudl-page">
        <HudlHeader />
        <TierLimitCta
          limit="hudl_integration"
          unit="exports"
          featureDescription="Hudl import and export"
          message={page.tierLimitError.message}
          currentPlan={typeof details.current_plan === 'string' ? details.current_plan : null}
          minimumPlan={typeof details.minimum_plan === 'string' ? details.minimum_plan : 'varsity'}
        >
          <p className="muted">
            Varsity and Elite teams can download any game as a Hudl playlist spreadsheet and bring
            Hudl breakdowns back into Gridiron.
          </p>
        </TierLimitCta>
      </div>
    );
  }

  if (reconcile !== null) {
    return (
      <div className="hudl-page hudl-page--wide">
        <nav className="hudl-crumbs" aria-label="Breadcrumb">
          <button type="button" className="hudl-crumbs__back" onClick={() => setReconcile(null)}>
            ← Hudl
          </button>
          <span aria-hidden="true">/</span>
          <span>Review import</span>
        </nav>
        <ReconcileView
          preview={reconcile.preview}
          initial={reconcile.result}
          overrides={reconcile.overrides}
          initialValueDecisions={reconcile.valueDecisions}
          onBack={() => setReconcile(null)}
          onDone={page.refreshImports}
        />
      </div>
    );
  }

  return (
    <div className="hudl-page" data-testid="hudl-page">
      <HudlHeader />

      {page.error === null ? null : (
        <div className="banner banner--error hudl-page__error" role="alert">
          <span>{page.error}</span>
          <button type="button" className="btn btn--secondary btn--sm" onClick={page.reload}>
            Try again
          </button>
        </div>
      )}

      {page.isLoading || page.fields === null ? (
        page.error === null ? (
          <div className="hudl-grid" aria-busy="true">
            <div className="desk-section desk-section--card hudl-card--skeleton" />
            <div className="desk-section desk-section--card hudl-card--skeleton" />
          </div>
        ) : null
      ) : (
        <div className="hudl-grid">
          <ExportCard
            events={page.events}
            fields={page.fields}
            templates={page.templates}
            setTemplates={page.setTemplates}
          />
          <ImportCard
            fields={page.fields.items}
            onReconcile={setReconcile}
            onImported={() => {
              page.refreshImports();
              page.refreshEvents();
            }}
          />
        </div>
      )}

      <DeskSection
        title="Recent imports"
        description={
          page.imports.length === 0
            ? undefined
            : `${page.imports.length} ${page.imports.length === 1 ? 'file' : 'files'} imported`
        }
        className="hudl-recent"
        data-testid="hudl-recent-section"
      >
        {page.importsError !== null ? (
          <p className="muted">{page.importsError}</p>
        ) : page.imports.length === 0 ? (
          <p className="muted hudl-recent__empty" data-testid="hudl-recent-empty">
            Nothing imported yet. Files you import show up here.
          </p>
        ) : (
          <div data-testid="hudl-recent">
            <DataTable<HudlImportRecord>
              caption="Recent imports"
              surface="hudl-imports"
              layoutName="Recent Hudl imports columns"
              columns={recentColumns}
              rows={sortedImports}
              getRowId={(record) => record.import_id}
              sort={recentSort}
              onSortChange={setRecentSort}
            />
          </div>
        )}
      </DeskSection>
    </div>
  );
}

function HudlHeader(): JSX.Element {
  return (
    <PageHeader
      title="Hudl"
      context="Send your game stats to Hudl, or bring a Hudl breakdown into Gridiron."
    />
  );
}

function addedCount(record: HudlImportRecord): number {
  return record.counts.created + record.counts.inserted;
}

function changesLabel(record: HudlImportRecord): string {
  const parts: string[] = [];
  if (record.counts.updated > 0) parts.push(`${record.counts.updated} updated`);
  if (record.counts.skipped > 0) parts.push(`${record.counts.skipped} left out`);
  return parts.length === 0 ? '—' : parts.join(' · ');
}

function sortImports(
  records: readonly HudlImportRecord[],
  sort: DataTableSort
): HudlImportRecord[] {
  const direction = sort.order === 'asc' ? 1 : -1;
  const key = (record: HudlImportRecord): string | number => {
    switch (sort.by) {
      case 'game':
        return record.event_name.toLowerCase();
      case 'file':
        return record.file_name.toLowerCase();
      case 'added':
        return addedCount(record);
      case 'by':
        return (record.created_by_name ?? '').toLowerCase();
      default:
        return record.created_at;
    }
  };
  return [...records].sort((left, right) => {
    const a = key(left);
    const b = key(right);
    return a < b ? -direction : a > b ? direction : 0;
  });
}
